import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { cn, showToast } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { useOrganisation } from '@my/directory-api/hooks';
import { useApproveOrdersForInvoicing, useOrdersForApproval } from '@my/tms-api/hooks';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { parseOrderReferenceLabels } from '@/utils/orderReferenceLabels';
import type { InvoiceTab } from '@/stores/invoiceView';
import { MOCK_APPROVAL_ORDERS, MOCK_FINANCE_ASSISTANT, MOCK_VAT_PCT } from '../lib/mockData';
import { money, moneyShort, orderNet } from '../lib/format';
import { exceptionLabel, pickPrimaryException, SEVERITY_RANK } from '../lib/approval';

interface FinanceAssistantPanelProps {
  open: boolean;
  /** Which tab the panel belongs to — drives the relevant suggestions shown. */
  tab: InvoiceTab;
  onClose: () => void;
}

const SUBTITLE: Record<InvoiceTab, string> = {
  approval: 'Approval flow',
  create: 'Invoice creation',
  summary: 'Invoices & collections',
};

// Processing animation — same cadence as the Manifesting suggestions panel.
const PROCESSING_STEPS = ['Analysing', 'Thinking', 'Building'] as const;
const PROCESSING_STEP_MS = 1000;

/**
 * Finance "my i" assistant — a sliding right-hand overlay (same pattern + width
 * as the Manifesting load-suggestions panel). Content is scoped to the active
 * tab: approval prompts on For-approval, build prompts on Create, chasing /
 * collections on Summary. Mock-driven for now.
 */
export function FinanceAssistantPanel({ open, tab, onClose }: FinanceAssistantPanelProps) {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  // For-approval: live queue (shares the tab's react-query cache) + the org's
  // reference labels, so the breakdown reads the same as the grid badges.
  const approvalQuery = useOrdersForApproval(tab === 'approval' && orgId ? orgId : null);
  const organisationQuery = useOrganisation(orgId);
  const approveMutation = useApproveOrdersForInvoicing();
  const refLabels = useMemo(
    () => parseOrderReferenceLabels(organisationQuery.data?.tmsOrderReferenceLabelsJson),
    [organisationQuery.data?.tmsOrderReferenceLabelsJson],
  );
  const approvalSummary = useMemo(() => {
    const orders = approvalQuery.data ?? [];
    const customers = new Set(orders.map((o) => o.customerPartyId)).size;
    // Bucket each order under its single most-important issue so the breakdown
    // partitions the queue — every order is counted once, and
    // (sum of breakdown) + (ready) === total orders.
    const byLabel = new Map<string, { label: string; count: number; severity: string }>();
    const readyIds: string[] = [];
    for (const o of orders) {
      const primary = pickPrimaryException(o.exceptions);
      if (!primary) {
        readyIds.push(o.id); // no warnings or blockers
        continue;
      }
      const label = exceptionLabel(primary, refLabels);
      const hit = byLabel.get(label);
      if (hit) hit.count += 1;
      else byLabel.set(label, { label, count: 1, severity: primary.severity });
    }
    // Blockers first, then by count descending.
    const breakdown = [...byLabel.values()].sort(
      (x, y) => (SEVERITY_RANK[y.severity]! - SEVERITY_RANK[x.severity]!) || y.count - x.count,
    );
    return { customers, orders: orders.length, breakdown, readyIds, needsAttention: orders.length - readyIds.length };
  }, [approvalQuery.data, refLabels]);

  const approveAllReady = () => {
    const ids = approvalSummary.readyIds;
    if (!orgId || ids.length === 0) return;
    approveMutation.mutate(
      { organisationId: orgId, orderIds: ids },
      {
        onSuccess: (res) => {
          if (res.approved.length > 0)
            showToast({
              type: 'success',
              message: `Approved ${res.approved.length} order${res.approved.length === 1 ? '' : 's'} for invoicing.`,
            });
        },
        onError: () => showToast({ type: 'error', message: 'Could not approve — please try again.' }),
      },
    );
  };

  // Processing animation (approval tab only): 3 steps × 1s, like Manifesting.
  const [scanning, setScanning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!open || tab !== 'approval') {
      setScanning(false);
      return;
    }
    setScanning(true);
    setStepIndex(0);
    const timers = PROCESSING_STEPS.slice(1).map((_, i) =>
      setTimeout(() => setStepIndex(i + 1), (i + 1) * PROCESSING_STEP_MS),
    );
    timers.push(setTimeout(() => setScanning(false), PROCESSING_STEPS.length * PROCESSING_STEP_MS));
    return () => timers.forEach(clearTimeout);
  }, [open, tab]);
  const stepLabel = PROCESSING_STEPS[stepIndex];

  // Create / Summary tabs are still mock-driven.
  const a = MOCK_FINANCE_ASSISTANT;
  const creditPct = Math.round((a.creditLimit.outstanding / a.creditLimit.limit) * 1000) / 10;
  const eligible = MOCK_APPROVAL_ORDERS.filter((o) => o.status === 'eligible');
  const eligibleGross = eligible.reduce((s, o) => s + orderNet(o) * (1 + MOCK_VAT_PCT / 100), 0);

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-40 flex w-[34rem] max-w-[92%] flex-col border-l border-slate-200 bg-slate-50 shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* "my i" bar — icon-only close, full-width dark. */}
      <div className="flex w-full items-center gap-2.5 border-b border-slate-700 bg-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close finance assistant"
          title="Close"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-700"
        >
          <MyIIcon state={scanning ? 'scanning' : 'idle'} className="h-7 w-7" />
        </button>
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-100">my i</div>
          <div className="text-[10px] text-slate-400">{SUBTITLE[tab]}</div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {/* ── For approval — processing sequence, then live summary ──────── */}
        {tab === 'approval' &&
          (scanning ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
              <MyIIcon state="scanning" className="h-16 w-16" />
              <p key={stepLabel} className="animate-pulse text-sm font-medium text-slate-600">
                {stepLabel}…
              </p>
              <p className="text-xs text-slate-400">Scanning the approval queue.</p>
            </div>
          ) : approvalSummary.orders === 0 ? (
            <p className="py-10 text-center text-sm italic text-slate-400">Nothing awaiting approval.</p>
          ) : (
            <>
              <Card>
                <CardTitle>Awaiting approval</CardTitle>
                <p className="mt-1 text-[11px] text-slate-500">
                  <span className="text-sm font-semibold text-slate-800">{approvalSummary.customers}</span>{' '}
                  customer{approvalSummary.customers === 1 ? '' : 's'} ·{' '}
                  <span className="text-sm font-semibold text-slate-800">{approvalSummary.orders}</span>{' '}
                  order{approvalSummary.orders === 1 ? '' : 's'}
                </p>
              </Card>

              <Card>
                <CardTitle>Breakdown</CardTitle>
                {approvalSummary.breakdown.length === 0 ? (
                  <p className="mt-0.5 text-[11px] text-emerald-700">
                    All clear — every order is ready to approve.
                  </p>
                ) : (
                  <>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-700">{approvalSummary.needsAttention}</span>{' '}
                      order{approvalSummary.needsAttention === 1 ? '' : 's'} need
                      {approvalSummary.needsAttention === 1 ? 's' : ''} attention — by main issue:
                    </p>
                    <ul className="mt-1.5 space-y-1">
                    {approvalSummary.breakdown.map((b) => (
                      <li key={b.label} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="text-slate-600">
                          <span
                            className={cn(
                              'text-xs font-semibold',
                              b.severity === 'Stop' ? 'text-red-700' : 'text-amber-700',
                            )}
                          >
                            {b.count}
                          </span>{' '}
                          with {b.label}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium',
                            b.severity === 'Stop'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-800',
                          )}
                        >
                          {b.severity === 'Stop' ? 'Blocker' : 'Warning'}
                        </span>
                      </li>
                    ))}
                    </ul>
                  </>
                )}
              </Card>

              <Card>
                <CardTitle>Ready to approve</CardTitle>
                <p className="mt-1 text-[11px] text-slate-500">
                  <span className="text-sm font-semibold text-emerald-700">
                    {approvalSummary.readyIds.length}
                  </span>{' '}
                  order{approvalSummary.readyIds.length === 1 ? '' : 's'} with no warnings or blockers.
                </p>
                <button
                  type="button"
                  onClick={approveAllReady}
                  disabled={approvalSummary.readyIds.length === 0 || approveMutation.isPending}
                  className="mt-2 w-full rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Approve all {approvalSummary.readyIds.length} order
                  {approvalSummary.readyIds.length === 1 ? '' : 's'}
                </button>
              </Card>
            </>
          ))}

        {/* ── Create invoices ──────────────────────────────────────────── */}
        {tab === 'create' && (
          <>
            <Card>
              <CardTitle>Ready to invoice</CardTitle>
              <p className="mt-0.5 text-[11px] text-slate-500">
                {eligible.length} approved orders · est. {money(eligibleGross, 'EUR')} incl. VAT.
                Batch by customer to cut the invoice count.
              </p>
              <CardAction
                label="Build all eligible"
                onClick={() => showToast({ type: 'success', message: 'Drafted invoices for all eligible customers.' })}
              />
            </Card>

            <Card tone="violet">
              <CardTitle>Credit limit — {a.creditLimit.customer}</CardTitle>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Outstanding {moneyShort(a.creditLimit.outstanding)} of {moneyShort(a.creditLimit.limit)} ({creditPct}%).
                Consider settling before raising more.
              </p>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.min(100, creditPct)}%` }} />
              </div>
            </Card>
          </>
        )}

        {/* ── Invoice summary ──────────────────────────────────────────── */}
        {tab === 'summary' && (
          <>
            <Card>
              <CardTitle>{a.overdue.length} overdue invoices</CardTitle>
              <ul className="mt-1 space-y-1">
                {a.overdue.map((o) => (
                  <li key={o.id} className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-600">
                      {o.customer} — <span className="text-slate-400">{o.number}</span>
                    </span>
                    <span className="tabular-nums text-slate-500">
                      {o.days}d · {money(o.amount, o.currency)}
                    </span>
                  </li>
                ))}
              </ul>
              <CardAction
                label="Chase all 3"
                onClick={() => showToast({ type: 'success', message: 'Chase emails sent for 3 overdue invoices.' })}
              />
            </Card>

            <Card>
              <CardTitle>This month</CardTitle>
              <dl className="mt-1 space-y-0.5 text-[11px]">
                <Row label="Invoiced" value={moneyShort(a.month.invoiced)} />
                <Row label="Collected" value={moneyShort(a.month.collected)} tone="success" />
                <Row label="Outstanding" value={moneyShort(a.month.outstanding)} />
                <Row label="Overdue" value={moneyShort(a.month.overdue)} tone="danger" />
              </dl>
            </Card>

            <Card tone="violet">
              <CardTitle>Credit limit — {a.creditLimit.customer}</CardTitle>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Outstanding {moneyShort(a.creditLimit.outstanding)} of {moneyShort(a.creditLimit.limit)} ({creditPct}%).
                Recommend hold or limit review.
              </p>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.min(100, creditPct)}%` }} />
              </div>
              <CardAction label="View account" onClick={() => showToast({ type: 'info', message: 'Customer account — coming soon.' })} />
            </Card>
          </>
        )}
      </div>
    </aside>
  );
}

function Card({ children, tone }: { children: React.ReactNode; tone?: 'violet' }) {
  return (
    <div className={cn('rounded-lg border bg-white p-3', tone === 'violet' ? 'border-violet-200' : 'border-slate-200')}>
      {children}
    </div>
  );
}
function CardTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-semibold text-slate-800">{children}</p>;
}
function CardAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mt-2 text-[11px] font-medium text-sky-700 hover:underline">
      → {label}
    </button>
  );
}
function Row({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'danger' }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={cn(
          'tabular-nums',
          tone === 'success' ? 'text-emerald-600' : tone === 'danger' ? 'text-red-600' : 'text-slate-700',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
